import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "share-instructions-page grid h-full w-full grid-rows-[auto,minmax(0,1fr),auto] justify-items-center overflow-hidden px-5 py-5 text-jm_white" }
const _hoisted_2 = { class: "share-instructions-header flex shrink-0 flex-col items-center" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "share-instructions-title mb-2 text-center font-bold uppercase leading-none" }
const _hoisted_5 = {
  key: 0,
  class: "share-instructions-description max-w-[18rem] text-center leading-6"
}
const _hoisted_6 = { class: "share-instructions-stage flex min-h-0 w-full max-w-[22rem] flex-col items-center justify-center py-3" }
const _hoisted_7 = { class: "share-instructions-preview relative flex items-center justify-center overflow-hidden rounded-[1.5rem] border-2 border-jm_white/40 bg-black/30 shadow-2xl" }
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 1,
  class: "flex h-full w-full items-center justify-center px-8 text-center text-sm uppercase tracking-[0.2em] text-jm_white/70"
}
const _hoisted_10 = { class: "share-instructions-steps mt-4 max-w-[20rem] text-center text-sm leading-6 text-jm_white/80" }
const _hoisted_11 = { class: "share-instructions-bartender mt-2 max-w-[20rem] text-center text-sm font-semibold leading-6 text-jm_white" }
const _hoisted_12 = {
  key: 0,
  class: "mt-4 text-center text-sm leading-5 text-jm_orange"
}
const _hoisted_13 = { class: "share-instructions-actions flex w-full max-w-[22rem] shrink-0 flex-col gap-3 self-end" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import logo from "@/assets/images/misc/logo_jaegermeister_white.svg";
import { useGlobalStore } from "@/stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { uploadCapturedPhoto } from "@/services/pimImageUpload";

type SharePayload = {
  files?: File[];
  title?: string;
  text?: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'shareInstructions',
  setup(__props) {

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();

const capturedPhoto = computed(() => globalStore.capturedPhotoDataUrl);
const shareError = ref("");
const isSharing = ref(false);
const didLeaveForShare = ref(false);
const finishFallbackTimer = ref<number | null>(null);

const dataUrlToFile = (dataUrl: string) => {
  const [header, base64Content] = dataUrl.split(",");

  if (!header || !base64Content) {
    throw new Error("The photo data could not be prepared for sharing.");
  }

  const mimeMatch = header.match(/data:(.*?);base64/);
  const mimeType = mimeMatch?.[1] || "image/jpeg";
  const binary = window.atob(base64Content);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], `jaegermeister-shot-${Date.now()}.jpg`, {
    type: mimeType,
  });
};

const createShareFile = () => {
  if (globalStore.capturedPhotoFile) {
    return globalStore.capturedPhotoFile;
  }

  if (!capturedPhoto.value) {
    throw new Error("Missing image data for share");
  }

  return dataUrlToFile(capturedPhoto.value);
};

const shareImageWithSystemShareSheet = async (shareFile: File) => {
  const nav = navigator as Navigator & {
    canShare?: (data?: SharePayload) => boolean;
    share: (data?: SharePayload) => Promise<void>;
  };
  const shareData: SharePayload = {
    files: [shareFile],
    title: "Jagermeister Shot Machine",
  };

  if (!nav.share) {
    throw new Error(
      "Sharing this photo is not supported on this device. Please save it manually from the preview."
    );
  }

  if (nav.canShare && !nav.canShare(shareData)) {
    throw new Error(
      "This device cannot share the generated photo file. Please save it manually from the preview."
    );
  }

  await nav.share(shareData);
};

const ensurePhotoUploaded = async () => {
  if (!capturedPhoto.value) {
    throw new Error("Missing image data for PIM upload");
  }

  if (globalStore.uploadedPhotoItemId !== null) {
    return globalStore.uploadedPhotoItemId;
  }

  const itemId = await uploadCapturedPhoto(capturedPhoto.value);
  globalStore.setUploadedPhotoItemId(itemId);
  kpiTracker.setEnteredCrmData(true);

  try {
    await session.updateSessionProgress(kpiTracker.getKpiData(), {
      step: "photo_uploaded",
    });
  } catch (error) {
    console.error("[ShareInstructions] Error while tracking photo upload:", error);
  }

  return itemId;
};

const finishRaffleFlow = () => {
  clearFinishFallbackTimer();
  globalStore.changeStep("raffleMove");
};

const clearFinishFallbackTimer = () => {
  if (finishFallbackTimer.value === null) {
    return;
  }

  window.clearTimeout(finishFallbackTimer.value);
  finishFallbackTimer.value = null;
};

const scheduleRaffleFallback = () => {
  clearFinishFallbackTimer();
  finishFallbackTimer.value = window.setTimeout(() => {
    if (globalStore.currentStep === "shareInstructions") {
      finishRaffleFlow();
    }
  }, 1200);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "hidden") {
    didLeaveForShare.value = true;
    return;
  }

  if (document.visibilityState === "visible" && didLeaveForShare.value) {
    didLeaveForShare.value = false;
    finishRaffleFlow();
  }
};

const handlePageHide = () => {
  didLeaveForShare.value = true;
};

const handlePageShowOrFocus = () => {
  if (didLeaveForShare.value) {
    didLeaveForShare.value = false;
    finishRaffleFlow();
  }
};

const handleInstagramShare = async () => {
  if (isSharing.value) {
    return;
  }

  if (!capturedPhoto.value) {
    globalStore.changeStep("photoCapture");
    return;
  }

  isSharing.value = true;
  shareError.value = "";

  try {
    const shareFile = createShareFile();
    await ensurePhotoUploaded();
    await shareImageWithSystemShareSheet(shareFile);
    scheduleRaffleFallback();
  } catch (error) {
    console.error("[ShareInstructions] Error while sharing image:", error);
    shareError.value =
      error instanceof Error
        ? error.message
        : "The photo could not be shared.";
  } finally {
    isSharing.value = false;
  }
};

const goBackToPhoto = () => {
  if (isSharing.value) {
    return;
  }

  globalStore.playTapSound();
  globalStore.changeStep("photoCapture");
};

onMounted(() => {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("pageshow", handlePageShowOrFocus);
  window.addEventListener("focus", handlePageShowOrFocus);
});

onBeforeUnmount(() => {
  clearFinishFallbackTimer();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pagehide", handlePageHide);
  window.removeEventListener("pageshow", handlePageShowOrFocus);
  window.removeEventListener("focus", handlePageShowOrFocus);
  if (globalStore.currentStep !== "raffleMove") {
    kpiTracker.resetTrackingData();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DefaultLayout, { "background-image": "green" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("img", {
            src: _unref(logo),
            alt: "logo",
            class: "share-instructions-logo mb-4 w-[92px]"
          }, null, 8, _hoisted_3),
          _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t("share_instructions.title")), 1),
          (_ctx.$t('share_instructions.description'))
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t("share_instructions.description")), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            (capturedPhoto.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: capturedPhoto.value,
                  alt: "Captured selfie",
                  class: "h-full w-full object-cover"
                }, null, 8, _hoisted_8))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_ctx.$t("photo_capture.placeholder")), 1))
          ]),
          _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t("share_instructions.steps")), 1),
          _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t("share_instructions.bartender_note")), 1),
          (shareError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(shareError.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("button", {
            class: _normalizeClass(["share-instructions-button min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white", { 'cursor-not-allowed opacity-50': isSharing.value }]),
            disabled: isSharing.value,
            onClick: handleInstagramShare
          }, _toDisplayString(isSharing.value
              ? _ctx.$t("share_instructions.sharing")
              : _ctx.$t("share_instructions.share_button")), 11, _hoisted_14),
          _createElementVNode("button", {
            class: _normalizeClass(["share-instructions-button min-h-12 rounded-xl border-2 border-jm_white/70 bg-white/10 px-4 text-sm font-bold uppercase tracking-[0.15em] text-jm_white backdrop-blur-sm", { 'cursor-not-allowed opacity-50': isSharing.value }]),
            disabled: isSharing.value,
            onClick: goBackToPhoto
          }, _toDisplayString(_ctx.$t("share_instructions.back_button")), 11, _hoisted_15)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})